import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/AuthContext";
import Ourdept_logo from "../../Images/Ourdept_logo.jpeg";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toIndianFormat = (num) => {
  if (!num && num !== 0) return "";
  const str = String(Math.floor(Number(num)));
  if (str.length <= 3) return str;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
};

const isClosingSoon = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  const end = new Date();
  end.setDate(today.getDate() + 14);
  return d >= today && d <= end;
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const map = {
    OPEN: {
      bg: "#ECFDF5",
      color: "#15803D",
      border: "#86EFAC",
      dot: "#22C55E",
      label: "Open",
    },
    CLOSED: {
      bg: "#F8FAFC",
      color: "#475569",
      border: "#CBD5E1",
      dot: "#94A3B8",
      label: "Closed",
    },
    DRAFT: {
      bg: "#FFFBEB",
      color: "#B45309",
      border: "#FDE68A",
      dot: "#F59E0B",
      label: "Draft",
    },
    CANCELLED: {
      bg: "#FFF1F2",
      color: "#BE123C",
      border: "#FECDD3",
      dot: "#F43F5E",
      label: "Cancelled",
    },
  };
  const cfg = map[status || ""] || map.OPEN;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
        border: `0.5px solid ${cfg.border}`,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
};

const SectionLabel = ({ children }) => (
  <p
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: "#64748b",
      marginBottom: 9,
      marginTop: 0,
    }}
  >
    {children}
  </p>
);

const DetailRow = ({ icon, label, value, valueStyle = {} }) => {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "9px 0",
        borderBottom: "0.5px solid #f1f5f9",
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#334155",
        }}
      >
        <Icon icon={icon} width={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <p
          style={{
            fontSize: 11,
            color: "#64748b",
            marginBottom: 3,
            marginTop: 0,
            fontWeight: 600,
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#0f172a",
            lineHeight: 1.4,
            margin: 0,
            ...valueStyle,
          }}
        >
          {value}
        </p>
      </div>
    </div>
  );
};

const SkillTag = ({ label }) => (
  <span
    style={{
      fontSize: 12,
      fontWeight: 600,
      padding: "4px 11px",
      borderRadius: 999,
      background: "#EFF6FF",
      color: "#1D4ED8",
      border: "0.5px solid #BFDBFE",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

const TextBlock = ({ text }) => (
  <div
    style={{
      background: "#f8fafc",
      borderRadius: 10,
      padding: "12px 14px",
      border: "0.5px solid #f1f5f9",
      maxHeight: 150,
      overflowY: "auto",
    }}
  >
    <p
      style={{
        fontSize: 13,
        color: "#334155",
        lineHeight: 1.7,
        margin: 0,
        whiteSpace: "pre-wrap",
      }}
    >
      {text}
    </p>
  </div>
);

// Share row: native share sheet where supported, plus direct-to-app links.
const ShareRow = ({ url, title }) => {
  const [open, setOpen] = useState(false);
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const shareText = `${title} — ${url}`;

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, text: title, url });
    } catch {
      // user cancelled or unsupported — ignore
    }
  };

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: "ic:baseline-whatsapp",
      color: "#25D366",
      href: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: "mdi:linkedin",
      color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      key: "email",
      label: "Email",
      icon: "tabler:mail",
      color: "#64748b",
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <button
        onClick={() =>
          canNativeShare ? handleNativeShare() : setOpen((o) => !o)
        }
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          width: "100%",
          padding: "10px 12px",
          borderRadius: 9,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
          border: "0.5px solid #e2e8f0",
          background: "#ffffff",
          color: "#374151",
          transition: "background 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#f1f5f9";
          e.currentTarget.style.borderColor = "#cbd5e1";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#ffffff";
          e.currentTarget.style.borderColor = "#e2e8f0";
        }}
      >
        <Icon icon="tabler:share-2" width={15} />
        Share
      </button>

      {!canNativeShare && open && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 10,
            marginTop: 9,
          }}
        >
          {channels.map((ch) => (
            <a
              key={ch.key}
              href={ch.href}
              target="_blank"
              rel="noopener noreferrer"
              title={`Share on ${ch.label}`}
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                border: "0.5px solid #e2e8f0",
                background: "#ffffff",
                color: ch.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                transition: "transform 0.12s, border-color 0.12s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <Icon icon={ch.icon} width={18} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const QRShareModal = ({ data, onClose }) => {
  const { url, post } = data;
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();

  const companyName = user?.companyName || null;
  const companyLogo = user?.logo
    ? user.logo.startsWith("data:")
      ? user.logo
      : `data:${user.logoType || "image/png"};base64,${user.logo}`
    : null;

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Failed to copy link");
    }
  }, [url]);

  // ── Derived ──
  const title =
    post.jobTitle || post.consultancyJobTitle || post.roleName || "Job Opening";
  const department = post.department || post.departmentName || null;
  const location =
    [post.branchCity, post.branchState, post.branchCountry]
      .filter(Boolean)
      .join(", ") || null;
  const employmentType = post.employmentType
    ? post.employmentType.replace(/_/g, " ")
    : null;
  const skills = post.skills
    ? post.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const qualList = Array.isArray(post.qualifications)
    ? post.qualifications
    : [];
  const qualDisplay =
    qualList.length > 0
      ? qualList.map((q) => q.qualificationName || q).join(", ")
      : null;
  const closing = isClosingSoon(post.expectedClosingTime);
  const closingDate = formatDate(post.expectedClosingTime);
  const closingDisplay = closingDate
    ? closing
      ? `${closingDate} · Closing soon`
      : closingDate
    : null;
  const hiringFlowName =
    post.hiringFlowName || post.hiringFlow?.hiringFlowName || null;

  const sharedTo = [];
  if (post.shareToCandidates) sharedTo.push("Candidates portal");
  if (post.postOnLinkedIn) sharedTo.push("LinkedIn");
  if (post.postOnNakuri || post.postOnNaukri) sharedTo.push("Naukri");
  if (post.consultancySharedResponseDTOList?.length)
    sharedTo.push(
      `${post.consultancySharedResponseDTOList.length} consultanc${post.consultancySharedResponseDTOList.length === 1 ? "y" : "ies"}`,
    );

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.65)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 920,
          maxHeight: "92vh",
          border: "0.5px solid #e2e8f0",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 0 0 0.5px rgba(15,23,42,0.06), 0 24px 56px rgba(15,23,42,0.22)",
        }}
      >
        {/* ─── HEADER — compact single row ─── */}
        <div
          style={{
            background: "#0f172a",
            padding: "16px 22px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            position: "relative",
            flexShrink: 0,
          }}
        >
          {/* Live dot */}
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#22c55e",
              flexShrink: 0,
              display: "inline-block",
            }}
          />

          {/* Title */}
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.25,
              margin: 0,
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </h2>

          {/* Inline meta badges */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {post.jobPostCode && (
              <span
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.55)",
                  fontFamily: "monospace",
                }}
              >
                #{post.jobPostCode}
              </span>
            )}
            {department && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "3px 10px",
                  borderRadius: 999,
                  border: "0.5px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.75)",
                  background: "rgba(255,255,255,0.08)",
                  whiteSpace: "nowrap",
                }}
              >
                {department}
              </span>
            )}
            {location && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "3px 10px",
                  borderRadius: 999,
                  border: "0.5px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.75)",
                  background: "rgba(255,255,255,0.08)",
                  whiteSpace: "nowrap",
                }}
              >
                <Icon icon="tabler:map-pin" width={12} />
                {location}
              </span>
            )}
            {employmentType && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "3px 10px",
                  borderRadius: 999,
                  border: "0.5px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.75)",
                  background: "rgba(255,255,255,0.08)",
                  whiteSpace: "nowrap",
                }}
              >
                {employmentType}
              </span>
            )}
            <StatusPill status={post.status} />
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              flexShrink: 0,
              border: "0.5px solid rgba(255,255,255,0.16)",
              background: "rgba(255,255,255,0.09)",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.18)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.09)";
              e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            }}
          >
            <Icon icon="tabler:x" width={16} />
          </button>
        </div>

        {/* ─── BODY — scrolls if content is tall, so nothing is ever hidden ─── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr",
            overflowY: "auto",
          }}
        >
          {/* QR Panel */}
          <div
            style={{
              padding: "22px 18px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              borderRight: "0.5px solid #f1f5f9",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                background: "#ffffff",
                padding: "14px 12px 12px",
                borderRadius: 14,
                border: "0.5px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
              }}
            >
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName || "Company logo"}
                  style={{
                    maxHeight: 100,
                    maxWidth: 150,
                    objectFit: "contain",
                  }}
                />
              ) : companyName ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#0f172a",
                    textAlign: "center",
                    maxWidth: 150,
                    lineHeight: 1.3,
                  }}
                >
                  {companyName}
                </p>
              ) : null}
              <div style={{ lineHeight: 0 }}>
                <QRCodeCanvas
                  value={url}
                  size={150}
                  level="H"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                />
              </div>
            </div>

            <p
              style={{
                fontSize: 12,
                color: "#64748b",
                textAlign: "center",
                lineHeight: 1.5,
                margin: 0,
                maxWidth: 170,
              }}
            >
              Scan with your phone to open &amp; apply
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                width: "100%",
              }}
            >
              <button
                onClick={handleCopy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 9,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.2s ease",
                  border: copied
                    ? "0.5px solid #16a34a"
                    : "0.5px solid #185FA5",
                  background: copied ? "#f0fdf4" : "#185FA5",
                  color: copied ? "#15803d" : "#ffffff",
                }}
              >
                <Icon
                  icon={copied ? "tabler:check" : "tabler:copy"}
                  width={15}
                />
                {copied ? "Copied!" : "Copy link"}
              </button>
              <button
                onClick={() => window.open(url, "_blank")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 9,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  border: "0.5px solid #e2e8f0",
                  background: "#ffffff",
                  color: "#374151",
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f1f5f9";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#ffffff";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <Icon icon="tabler:external-link" width={15} />
                View listing
              </button>

              <ShareRow url={url} title={title} />
            </div>

            {sharedTo.length > 0 && (
              <div
                style={{
                  width: "100%",
                  borderTop: "0.5px solid #e9eef4",
                  paddingTop: 13,
                  marginTop: "auto",
                }}
              >
                <SectionLabel>Published on</SectionLabel>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {sharedTo.map((ch) => (
                    <div
                      key={ch}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        fontSize: 13,
                        color: "#334155",
                        fontWeight: 600,
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#22c55e",
                          flexShrink: 0,
                          display: "inline-block",
                        }}
                      />
                      {ch}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                fontSize: 12,
                color: "#cbd5e1",
                fontWeight: 500,
              }}
            >
              <span>Powered by OurDept.com</span>

              <img
                src={Ourdept_logo}
                alt="Ourdept"
                style={{
                  height: 40,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          {/* Details Panel — 2-col grid so all fits on screen */}
          <div
            style={{
              padding: "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {/* Top: details 2-col + description side by side */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0,
                columnGap: 2,
              }}
            >
              {/* Left col — core details */}
              <div>
                <SectionLabel>Job details</SectionLabel>
                <DetailRow
                  icon="tabler:users"
                  label="Openings"
                  value={
                    post.noOfVacancy
                      ? `${post.noOfVacancy} position${post.noOfVacancy !== 1 ? "s" : ""}`
                      : null
                  }
                />
                <DetailRow
                  icon="tabler:clock"
                  label="Experience"
                  value={post.experience}
                />
                <DetailRow
                  icon="tabler:currency-rupee"
                  label={`Salary · ${post.salaryType || "CTC"} · ${post.budgetType === "MONTHLY" ? "Monthly" : "Annual"}`}
                  value={
                    post.budget > 0
                      ? `₹ ${toIndianFormat(post.budget)} ${post.budgetType === "MONTHLY" ? "/ mo" : "/ yr"}`
                      : null
                  }
                  valueStyle={{ color: "#15803d" }}
                />
                {post.budgetType === "MONTHLY" && post.budget > 0 && (
                  <DetailRow
                    icon="tabler:calculator"
                    label="Annual equivalent"
                    value={`₹ ${toIndianFormat(post.budget * 12)} / year`}
                    valueStyle={{ color: "#475569", fontSize: 13 }}
                  />
                )}
                <DetailRow
                  icon="tabler:school"
                  label="Qualification"
                  value={qualDisplay}
                />
                <DetailRow
                  icon="tabler:id-badge"
                  label="Role"
                  value={post.roleName}
                />
                <DetailRow
                  icon="tabler:calendar-due"
                  label="Apply by"
                  value={closingDisplay}
                  valueStyle={closing ? { color: "#b45309" } : {}}
                />
                {hiringFlowName && (
                  <DetailRow
                    icon="tabler:git-branch"
                    label="Hiring flow"
                    value={hiringFlowName}
                  />
                )}
              </div>

              {/* Right col — text content */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {post.jobDescription && (
                  <div>
                    <SectionLabel>About the role</SectionLabel>
                    <TextBlock text={post.jobDescription} />
                  </div>
                )}
                {post.responsibilities && (
                  <div>
                    <SectionLabel>Responsibilities</SectionLabel>
                    <TextBlock text={post.responsibilities} />
                  </div>
                )}
                {post.desiredProfile && (
                  <div>
                    <SectionLabel>Ideal candidate</SectionLabel>
                    <TextBlock text={post.desiredProfile} />
                  </div>
                )}
              </div>
            </div>

            {/* Skills — full width below */}
            {skills.length > 0 && (
              <div style={{ borderTop: "0.5px solid #f1f5f9", paddingTop: 14 }}>
                <SectionLabel>Skills &amp; expertise</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {skills.map((s) => (
                    <SkillTag key={s} label={s} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default QRShareModal;
